import React, { useEffect, useState } from "react";
import axiosInstance, { handleApiError } from "../../services/axiosService";
import { toastSuccess } from "../../utils/toastUtils";
import { useParams } from "react-router-dom";
import Loader from "../../Layouts/Loader/Loader";
import AppLeftSideBar from "../../components/SideBar/AppLeftSideBar";
import ButtonActionIcon from "../../components/BuilderComponents/ButtonActionIcon";
import InfoTooltip from "../../components/BuilderComponents/InfoTooltip";
import { useModalContext } from "../../context/ModalContext";
import SingleSelectBox from "../../components/FormComponents/SingleSelectBox";
import SearchQuery from "../../components/BuilderComponents/SearchQuery";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";

const OrgStructureUser = () => {
  const [nonAssignUsers, setNonAssignUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [allOrgUsers, setAllOrgUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hierarchyData, setHierarchyData] = useState({});
  const { getUserConfirmationForDelete } = useModalContext();
  const [filteredNonAssignedUsers, setFilteredNonAssignedUsers] = useState(nonAssignUsers);
  const [filteredSelectedUsers, setFilteredSelectedUsers] = useState(selectedUsers);

  const { orgId } = useParams();
  const filterNonAssignedUsers = (assignUsers) => {
    return allOrgUsers.filter((item) => !assignUsers.some((user) => user.user_id === item.user_id));
  };

  const getAllOrgUsers = async () => {
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get(`/org/${orgId}/users`);
      setIsLoading(false);
      setAllOrgUsers(data);
    } catch (error) {
      handleApiError(error);
    }
  };

  const getUserHierarchy = async () => {
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get(`/org/${orgId}/users-hierarchy?depth=100`);
      setIsLoading(false);
      setHierarchyData(data);
    } catch (error) {
      handleApiError(error);
    }
  };

  const getTreeUsers = (user) => {
    let result = [{ user_id: user.user_id, name: user.name, manager: getManagerName(user.manager_id) }];

    if (user.children && user.children.length > 0) {
      user.children.forEach((child) => {
        result = result.concat(getTreeUsers(child));
      });
    }

    return result;
  };
  const getManagerName = (manager_id) => {
    const manager = allOrgUsers.find((user) => user.user_id === manager_id);
    return manager?.name || "(root)";
  };
  const updateManager = async (id, manager, root = false) => {
    try {
      const obj = {
        manager_id: manager,
        is_root: root
      };

      const { message } = await axiosInstance.patch(`/user-service/org/${orgId}/user/${id}`, obj);
      const user = allOrgUsers.find((user) => user.user_id === id);
      if (user) {
        // const removeDuplicate=
        const updatedSelectedUsers = [
          ...selectedUsers.filter((selectedUser) => selectedUser.user_id !== id),
          {
            user_id: user.user_id,
            name: user.name,
            manager: getManagerName(manager)
          }
        ];

        setSelectedUsers(updatedSelectedUsers);

        setNonAssignUsers(nonAssignUsers.filter((user) => user.user_id !== id));
      }
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const removeUserFromOrg = async (userId) => {
    try {
      const userConfirmed = await getUserConfirmationForDelete("", "", "remove");
      if (!userConfirmed) return;
      const { message } = await axiosInstance.delete(`/user-service/org/${orgId}/user/${userId}`);
      const filterNonAssignedUsers = nonAssignUsers.filter((user) => user.user_id !== userId);
      setNonAssignUsers(filterNonAssignedUsers);
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const removeManager = async (id) => {
    try {
      const obj = {
        manager_id: "",
        is_root: false
      };
      const userConfirmed = await getUserConfirmationForDelete("", "", "remove");
      if (!userConfirmed) return;
      const { message } = await axiosInstance.patch(`/user-service/org/${orgId}/user/${id}`, obj);
      const filterUser = selectedUsers.filter((user) => user.user_id !== id);
      const user = allOrgUsers.find((user) => user.user_id === id);
      setNonAssignUsers([...nonAssignUsers, user]);
      setSelectedUsers(filterUser);
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await getUserHierarchy();
      await getAllOrgUsers();
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Object.keys(hierarchyData).length > 0) {
      const assignUsers = getTreeUsers(hierarchyData);
      setSelectedUsers(assignUsers);
      setNonAssignUsers(filterNonAssignedUsers(assignUsers));
    } else {
      setNonAssignUsers(filterNonAssignedUsers([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hierarchyData, allOrgUsers]);

  return (
    <div className="application_container">
      <AppLeftSideBar type="app" />
      {isLoading ? (
        <Loader />
      ) : (
        <div className="dashboard_outer_section">
          <div className="d_flex w_100 gap_4 p_4 h_100">
            <div className="builder_form h_100 overflow_auto" style={{ flex: "1" }}>
              <label className="builder_label">Assigned users (in org structure)</label>
              <div className="w_50 mb_2">
                <SearchQuery
                  data={selectedUsers}
                  searchKey="name"
                  onSearch={setFilteredSelectedUsers}
                  placeholder="Search Assigned Users"
                />
              </div>
              <div className="user_table user-list">
                {selectedUsers.length === 0 && (
                  <SingleSelectBox
                    selectedData={""}
                    options={allOrgUsers}
                    valueKey="user_id"
                    onChange={(obj) => updateManager(obj.user_id, "", true)}
                    placeholder="Select root"
                    isSearch={true}
                  />
                )}
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Manager</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSelectedUsers.length > 0 ? (
                      filteredSelectedUsers.map((user) => {
                        return (
                          <tr key={user.user_id}>
                            <td>{user.name}</td>
                            <td>{user.manager}</td>
                            <td>
                              <div className="icons">
                                <MaterialIcon
                                  name="delete"
                                  className="builder_action_btn delete"
                                  onClick={() => removeManager(user.user_id)}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td>No Users Found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div style={{ flex: "1" }} className="builder_form">
              <label className="builder_label">Non assigned users</label>
              <div className="w_50 mb_2">
                <SearchQuery
                  data={nonAssignUsers}
                  searchKey="name"
                  onSearch={setFilteredNonAssignedUsers}
                  placeholder="Search Non Assigned Users"
                />
              </div>
              <div className="user_table">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Assign Manager</th>
                      <th>
                        <div className="d_flex align_center gap_1">
                          Remove Org Access
                          <InfoTooltip documentKey="remove_org_user" />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredNonAssignedUsers.length > 0 ? (
                      filteredNonAssignedUsers.map((user) => (
                        <NonAssignedUserRow
                          key={user._id}
                          user={user}
                          selectedUsers={selectedUsers}
                          updateManager={updateManager}
                          removeAcces={removeUserFromOrg}
                        />
                      ))
                    ) : (
                      <tr>
                        <td>No Users Found</td>
                        <td></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrgStructureUser;

const NonAssignedUserRow = ({ user, selectedUsers, updateManager, removeAcces }) => {
  const [manager, setManager] = useState("");
  const handleManagerChange = (value) => {
    setManager(value);
    updateManager(user.user_id, value);
  };

  useEffect(() => {
    setManager("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUsers.length]);

  return (
    <tr key={user.user_id}>
      <td>{user.name}</td>
      <td>
        <div className="builder_dropdown_outer mb_0">
          <SingleSelectBox
            selectedData={manager}
            options={selectedUsers}
            valueKey="user_id"
            onChange={(obj) => handleManagerChange(obj.user_id)}
            placeholder="Select manager"
            isSearch={true}
          />
        </div>
      </td>
      <td>
        <ButtonActionIcon type="delete" onClick={() => removeAcces(user.user_id)} />
      </td>
    </tr>
  );
};
